 import React from 'react';
 import DefaultLayout from '../../layouts/DefaultLayout';
 import TemplateColumn, { TemplateColumnWithSidebarContainer } from '../../layouts/TemplateColumn';
 import { PageHeader, PageSection, RelatedContent, CrossLink, IndicationPageIntro, esokeynotecomparison, ComparisonTable, PageSubSection, AnchorLinks, PrimaryCallOut, SecondaryCallout, KMCurve, TextBlock } from '../../components';
 import GEJ from '../../components/StudyDesign/__study-design/gej';
 import { jobCodes } from '../../util/data/jobCodes';
 import { StaticImage } from 'gatsby-plugin-image';
import { Heading } from '../../util/typography';
import styled from 'styled-components';
import { createMarkup } from '../../util/functions';

 // SEO INFORMATION
 const pageMeta = {
     title: `KEYNOTE-859 - Clinical Trial Results | HCP`,
     keywords: `keynote 859, keynote-859, kn-859`,
     description: `Health care professionals may review findings from KEYNOTE-859, a clinical trial for patients with locally advanced or metastatic HER2-negative gastric or gastroesophageal junction (GEJ) adenocarcinoma.`,
     schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/gastric-adenocarcinoma/","@type":"ApprovedIndication","name":"KEYTRUDA®","alternateName":"pembrolizumab","description":"KEYTRUDA, in combination with fluoropyrimidine- and platinum-containing chemotherapy, is indicated for the first-line treatment of adults with locally advanced unresectable or metastatic HER2-negative gastric or gastroesophageal junction (GEJ) adenocarcinoma whose tumors express PD-L1 (CPS ≥1) as determined by an FDA-approved test. "}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/gastric-adenocarcinoma/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/efficacy/gastric-adenocarcinoma/","@type":"MedicalWebPage","name":"Study Design for KEYNOTE-859","description":"The efficacy of KEYTRUDA in combination with fluoropyrimidine- and platinum-containing chemotherapy was investigated in KEYNOTE-859, a Phase 3, multicenter, randomized, double-blind, placebo-controlled trial that enrolled 1,579 patients with HER2-negative advanced gastric or gastroesophageal junction (GEJ) adenocarcinoma who had not previously received systemic therapy for metastatic disease. Patients with an autoimmune disease that required systemic therapy within 2 years of treatment or a medical condition that required immunosuppression were ineligible. Randomization was stratified by PD-L1 expression (CPS ≥1 or CPS <1), chemotherapy regimen (5-FU plus cisplatin [FP] or capecitabine plus oxaliplatin [CAPOX]) and geographic region (Europe/Israel/North America/Australia, Asia, or Rest of the World)."}`
     ]
 }

 //from /src/util/data/indications.js
 const indicationId = 18;
 const jobCode = jobCodes[18].jobCode;
 const textBlock2 = `Patients with PD-L1 CPS >1 (n=344): An exploratory analysis of OS in the PD-L1 CPS <1 population showed a HR of 0.92 (95% CI 0.73, 1.17) indicating that the improvement in the ITT population was primarily attributed to the results observed in the subgroup of patients with PD-L1 CPS ≥1.`;

 const relatedContent = [
     {
         section: 'Resources',
         link: {
             url: '/resources/mechanism-of-action/',
             label: 'Mechanism of Action'
         }
     },
     {
         section: 'Resources',
         link: {
             url: 'https://www.merckaccessprogram-keytruda.com/',
             label: 'Access & Reimbursement Support'
         }
     }
 ];

 const crossLinkContent = {
     sections: [
        {
            heading: "Additional Clinical Data",
            items: [
                { text: '1L Combo Therapy - Advanced Esophageal or GEJ Carcinoma', url: '/efficacy/esophageal-carcinoma/' },
                { text: 'Upper GI NCCN Recommendations', url: '/static/pdf/Upper-GI-NCCN.pdf', target: '_blank' }
            ]
        },
        {
             heading: "Related Information",
             items: [
                 { text: 'Selected Adverse Reactions', url: '/safety/adverse-reactions/gastric-adenocarcinoma/' },
                 { text: 'Dosing', url: '/dosing/options/#her2-n' },
             ]
         },
     ],
     interestLinks: {
         heading: "You Might Be Interested In",
         items: [
             { eyebrow: 'Resources', text: 'Mechanism of Action', url: '/resources/mechanism-of-action/' },
         ],
     }
 };

 const anchorLinkModelData = [
    { label: 'Clinical Findings from KEYNOTE&#8288;-&#8288;859' }, 
    { label: 'Study Design for KEYNOTE&#8288;-&#8288;859' }, 
];

const gastricFootnote = [
    {
        label: "a.",
        text: "Based on the stratified Cox proportional hazard model"
    },
    {
        label: "b.",
        text: "One-sided <i>P</i> value based on stratified log-rank test."
    },
    {
        label: "c.",
        text: "Investigator’s choice of 5-FU plus cisplatin (FP) or oxaliplatin plus capecitabine (CAPOX)."
    },
    {
        label: "d.",
        text: "Response: Best objective response as confirmed complete response or partial response."
    },
    {
        label: "e.",
        text: "One-sided <i>P</i> value based on stratified Miettinen & Nurminen method."
    },
    {
        label: "f.",
        text: "Based on Kaplan-Meier estimates"
    },
];


const KMFootnotes = [
    {
        label: "a.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "b.",
        text: "One-sided p-Value based on stratified log-rank test"
    },
    {
        label: "c.",
        text: "Investigator’s choice of 5-FU plus cisplatin (FP) or oxaliplatin plus capecitabine (CAPOX). "
    },
];

const ComparisonTable3Footnotes = [
    {
        label: "k.",
        text: "Based on the stratified Cox proportional hazard model."
    },
    {
        label: "l.",
        text: "One-sided <i>P</i> value based on a stratified log-rank test."
    },
    {
        label: "m.",
        text: "Best objective response as confirmed complete response or partial response."
    },
    {
        label: "n.",
        text: "One-sided <i>P</i> value based on stratified Miettinen & Nurminen method."
    },
    {
        label: "o.",
        text: "Based on Kaplan-Meier estimation."
    },
];

const SecondaryCalloutWrapper = styled.div`
> div {
   margin-top: calc(14.10px + 0.60%);
}
`;

const PrimaryCalloutWrapper = styled.div`
h2 {
margin-bottom: 0;
}
@media(min-width: 768px){
>section{
padding:40px 0 !important;
}
}
@media(max-width: 767px){
>section{
padding:30px 0 !important;
}
}
`;

const ContentWrapper = styled.div`
margin-top: -10px;
`;
 const Page = ({ location }) => {

     return (
         <DefaultLayout 
         jobCode={jobCode} 
         indicationId={indicationId} 
         pageMeta={ pageMeta } 
         location={ location }
         >
             <div data-template-name="template-e">

                 {/* Begin Intro Column */}
                 <TemplateColumn id="template-e-intro" maxWidth={824}>
                     <PageHeader title="Clinical Trial Results" />
                     <PageSection bgColor="white">
                         <IndicationPageIntro 
                            indicationId={indicationId} 
                         />
                         <AnchorLinks items={anchorLinkModelData} />
                     </PageSection>
                     <PrimaryCalloutWrapper>
                     <PageSection bgColor="gradient" >                       
                            <PrimaryCallOut
                            title="Superior OS in 1L Treatment With KEYTRUDA + Chemotherapy vs Chemotherapy Alone in Advanced HER2-Negative Gastric or GEJ Adenocarcinoma (PD-L1 Expressors)"
                        />                                              
                    </PageSection>
                    </PrimaryCalloutWrapper>
                     {/* <PageSection bgColor="gradient">
                      <PrimaryClaim theme="light">Superior Overall Survival with KEYTRUDA + FP or CAPOX vs FP or CAPOX alone</PrimaryClaim>
                     </PageSection> */}
                 </TemplateColumn>
                 {/* End Intro Column */}

                 <TemplateColumnWithSidebarContainer>
                     {/* Begin Main Column */}
                     <TemplateColumn id="template-e-main" >
                        <PageSection title={anchorLinkModelData[0].label} bgColor="cloud">
                            <PageSubSection>
                                <SecondaryCalloutWrapper>
                                    <Heading.H4 as='H3' dangerouslySetInnerHTML={createMarkup('Patients with PD-L1 CPS ≥1 (n=1,235)')} />
                                </SecondaryCalloutWrapper>
                                <SecondaryCallout
                                    title="26% reduction in the risk of death with KEYTRUDA + chemotherapy vs chemotherapy alone"
                                />
                            </PageSubSection>
                                
                            {/* <PageSubSection showDivider={false}> */}
                                <KMCurve
                                    title="Kaplan-Meier curve of OS in patients with PD⁠&#8288;-&#8288;⁠L1 CPS ≥1"
                                    hr="HR<sup>a</sup>=0.74 (95% CI, 0.65–0.84; <i>P</i><sup>b</sup><0.0001)"
                                    image={() => <StaticImage 
                                        src="../../assets/KN859_CPS_1_KM_Curve.png"
                                        placeholder="none"
                                        alt="Kaplan-Meier Estimates of Overall Survival (OS) in Patients With PD-L1 CPS ≥1 from KEYNOTE-859" />
                                    }
                                    additionalInformation={[
                                        {
                                            text:"<p>Median OS in months (95% CI): 13.0 (11.6–14.2) with KEYTRUDA + chemo<sup>c</sup> vs 11.4 (10.5–12.0) with chemo<sup>c</sup> alone (HR<sup>a</sup>=0.74; 95% CI, 0.65–0.84; <i>P</i><sup>b</sup><0.0001).</p><p>Events observed: 75% (464/618) with KEYTRUDA + chemo<sup>c</sup> vs 85% (526/617) with chemo<sup>c</sup> alone.</p>"
                                        },]}
                                    footnotes = {KMFootnotes}
                                />
                                <ContentWrapper>
                                <TextBlock 
                                   definitions='1L = first line; 5-FU = 5-fluorouracil; CI = confidence interval; CPS = combined positive score; GEJ = gastroesophageal junction; HER2 = human epidermal growth factor receptor 2; HR = hazard ratio; OS = overall survival; PD⁠-⁠L1 = programmed death ligand 1.'
                                    >
                                <p><b>ITT (N= 1,579):</b>  A statistically significant improvement in OS, PFS, and ORR was demonstrated in patients randomized to KEYTRUDA in combination with chemotherapy compared with placebo in combination with chemotherapy at the time of a pre-specified interim analysis of OS.</p>
                                <p><b>CPS &lt; 1 (n=344):</b> An exploratory analysis of OS in the PD-L1 CPS &lt;1 population showed a HR of 0.92 (95% CI 0.73, 1.17) indicating that the improvement in the ITT population was primarily attributed to the results observed in the subgroup of patients with PD-L1 CPS ≥1.</p>                               
                                </TextBlock>
                                </ContentWrapper>

                                <SecondaryCalloutWrapper>
                                    <Heading.H4 as='H2' dangerouslySetInnerHTML={createMarkup('Patients with PD-L1 CPS ≥10 (n=551)')} />
                                </SecondaryCalloutWrapper>
                                <SecondaryCallout
                                    title="35% reduction in the risk of death with KEYTRUDA + chemotherapy vs chemotherapy alone"
                                />

                                <KMCurve
                                    title="Kaplan-Meier curve of OS in patients with PD⁠-⁠L1 CPS ≥10"
                                    hr="HR<sup>a</sup>=0.65 (95% CI, 0.53–0.79; <i>P</i><sup>b</sup><0.0001)"
                                    image={() => <StaticImage 
                                        src="../../assets/GASG233110_KN859_OS_Restricted_v6.png"
                                        placeholder="none"
                                        alt="Kaplan-Meier Estimates of Overall Survival (OS) in Patients With PD-L1 CPS ≥10 from KEYNOTE-859" />
                                    }
                                    additionalInformation={[
                                        {
                                            text:"<p>Median OS in months (95% CI): 15.7 (13.8–19.3) with KEYTRUDA + chemo<sup>c</sup> vs 11.8 (10.3–12.7) with chemo<sup>c</sup> alone (HR<sup>a</sup>=0.65; 95% CI, 0.53–0.79; <i>P</i><sup>b</sup><0.0001).</p><p>Events observed: 67% (188/279) with KEYTRUDA + chemo<sup>c</sup> vs 83% (226/272) with chemo<sup>c</sup> alone.</p>"
                                        },]}
                                />
                        
                                <TextBlock footnotes={KMFootnotes}>
                                </TextBlock>
                                
                                <TextBlock>
                                    {/* <p>ITT population (N= 1,579): A statistically significant improvement in OS, PFS, and ORR was demonstrated in patients randomized to KEYTRUDA in combination with chemotherapy compared with placebo in combination with chemotherapy at the time of a pre-specified interim analysis of OS.</p> */}
                                    {/* <p>{textBlock2}</p> */}
                                </TextBlock>
                                
                                <ComparisonTable  
                                title= 'Progression-Free Survival in Patients with CPS ≥1'                            
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA + Chemotherapy<sup>c</sup> (n=618)',
                                            },
                                            {
                                                legendTitle: 'Chemotherapy<sup>c</sup> alone (n=617)',
                                            },
                                        ],
                                        [
                                            {
                                                colspan: 2,
                                                hrCiP: 'HR<sup>a</sup>=0.72 (95% CI, 0.63-0.82; p<sup>b</sup><0.0001)',
                                                centerText: true
                                            },
                                        ],                                      
                                        [
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '443 (72%)',
                                            },
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '483 (78%)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '6.9 (6.0- 7.2)'
                                            },
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '5.6 (5.4-5.7)'
                                            },
                                        ],                                       
                                    ]
                                
                                } 
                            />
                                <ComparisonTable 
                                title='Progression-Free Survival in Patients with CPS ≥10'                          
                                data={
                                    [
                                        [
                                            {
                                                legendTitle: 'KEYTRUDA + Chemotherapy<sup>c</sup> (n=279)',
                                            },
                                            {
                                                legendTitle: 'Chemotherapy<sup>c</sup> alone (n=272)',
                                            },
                                        ],
                                        [
                                            {
                                                colspan: 2,
                                                centerText: true,
                                                hrCiP: 'HR<sup>a</sup>=0.62 (95% CI, 0.51-0.76; p<sup>b</sup><0.0001)',
                                            },   
                                        ],
                                        [
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '190 (68%)',
                                            },
                                            {
                                                label: 'Number of patients with event (%)',
                                                dataPoint: '210 (77%)',
                                            },
                                        ],
                                        [
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '8.1 (6.8-8.5)'
                                            },
                                            {
                                                label: 'Median in months (95% CI)',
                                                dataPoint: '5.6 (5.4-6.7)'
                                            },
                                        ],
                                    ]
                                
                                } 
                            />
                                <TextBlock footnotes={gastricFootnote} definitions="+Denotes ongoing response <p>ORR = objective response rate; PFS = progression-free survival. </p>">
                                    <h4>Objective Response Rate<sup>d</sup> and Duration of Response in Patients with CPS ≥1</h4>
                                    <p>ORR (95% CI) was 52% (48–56) with KEYTRUDA + chemo vs 43% (39–47) (P=0.0004<sup>e</sup>) with chemo alone. There were 10% complete responses with KEYTRUDA + chemo vs 6% complete responses with chemo alone and 42% partial responses with KEYTRUDA + chemo vs 37% partial responses with chemo alone. The median in months<sup>f</sup> (95% CI) for DOR was 8.3 (7.0–10.9) months with KEYTRUDA + chemo vs 5.6 (5.4–6.9) months with chemo alone and range in months was 1.2+ to 41.5+ with KEYTRUDA + chemo vs 1.3+ to 34.2+ with chemo alone.</p>
                                    <br />
                                    <h4>Objective Response Rate<sup>d</sup> and Duration of Response in Patients with CPS ≥10</h4>
                                    <p>ORR (95% CI) was 61% (55–66) with KEYTRUDA + chemo vs 43% (37–49) (P&lt;0.0001<sup>e</sup>) with chemo alone. There were 13% complete responses with KEYTRUDA + chemo vs 5% complete responses with chemo alone and 48% partial responses with KEYTRUDA + chemo vs 38% partial responses with chemo alone. The median in months<sup>f</sup> (95% CI) for DOR was 10.9 (8.0–13.8) months with KEYTRUDA + chemo vs 5.8 (5.3–7.0) months with chemo alone and range in months was 1.2+ to 41.5+ with KEYTRUDA + chemo vs 1.4+ to 31.2+ with chemo alone.</p>
                                </TextBlock>                   
                            {/* </PageSubSection> */}

                        </PageSection>
                        {/* END PAGE SECTION */}

                        <PageSection
                            title={anchorLinkModelData[1].label}
                            bgColor='white'
                        >
                            <PageSubSection>
                              <GEJ />
                            </PageSubSection>
                        </PageSection>
                     </TemplateColumn>

                     {/* End Main Column */}
                 <TemplateColumn id="template-e-sidebar">
                         <PageSection bgColor="mist">
                             <CrossLink {...crossLinkContent} />
                         </PageSection>
                </TemplateColumn>

                 </TemplateColumnWithSidebarContainer>

                 <TemplateColumn id="template-e-secondary">
                     <PageSection bgColor="keytrudaGreen">
                         <RelatedContent items={relatedContent} />
                     </PageSection>
                 </TemplateColumn>
             </div>
         </DefaultLayout>
     );
 };

 export default Page;
